import { Box, Chip, type Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Icon } from 'iconsax-react';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

export type TagVariant =
  | 'Blue'
  | 'Brown'
  | 'Cherry'
  | 'Gray'
  | 'Orange'
  | 'Tertiary'
  | 'New'
  | 'Purple'
  | 'Red'
  | 'Secondary'
  | 'Success'
  | 'White'
  | 'WhiteBlack'
  | 'Yellow'
  | 'Other'
  | 'Rejected'
  | 'Revision'
  | 'Approved'
  | 'Project'
  | 'Contact';

export type TagSize = 'sm' | 'md';

type VariantStyle = {
  backgroundColor: string;
  color: string;
  borderColor?: string;
};

const getVariantStyles = (theme: Theme): { [key in TagVariant]: VariantStyle } => ({
  Blue: {
    backgroundColor: theme.palette.SlabBlue[100],
    color: theme.palette.SlabTurquoise[600],
  },
  Brown: {
    backgroundColor: theme.palette.SlabBrown[200],
    color: theme.palette.SlabBrown[800],
  },
  Cherry: {
    backgroundColor: theme.palette.SlabCherry[100],
    color: theme.palette.SlabCherry[600],
  },
  Gray: {
    backgroundColor: theme.palette.SlabGray[0],
    color: theme.palette.SlabGray[600],
    borderColor: theme.palette.SlabGray[600],
  },
  Orange: {
    backgroundColor: theme.palette.SlabYellow[100],
    color: theme.palette.SlabYellow[800],
  },
  New: {
    backgroundColor: theme.palette.SlabIndigo[700],
    color: theme.palette.SlabGray[0],
  },
  Tertiary: {
    backgroundColor: theme.palette.SlabGray.Table,
    color: theme.palette.SlabGray[800],
  },
  Purple: {
    backgroundColor: theme.palette.SlabPurple[200],
    color: theme.palette.SlabPurple[600],
  },
  Red: {
    backgroundColor: theme.palette.SlabRed[100],
    color: theme.palette.SlabRed[500],
  },
  Secondary: {
    backgroundColor: theme.palette.SlabGray.Stroke,
    color: theme.palette.SlabGray[800],
  },
  Success: {
    backgroundColor: theme.palette.SlabGreen[100],
    color: theme.palette.SlabGreen[600],
  },
  White: {
    backgroundColor: theme.palette.SlabGray[0],
    color: theme.palette.SlabGray[600],
  },
  WhiteBlack: {
    backgroundColor: theme.palette.SlabGray[0],
    color: theme.palette.SlabGray[900],
    borderColor: theme.palette.SlabGray[900],
  },
  Yellow: {
    backgroundColor: theme.palette.SlabYellow[300],
    color: theme.palette.SlabBrown[600],
  },
  Other: {
    backgroundColor: theme.palette.SlabTurquoise[600],
    color: theme.palette.SlabGray[0],
  },
  Rejected: {
    backgroundColor: theme.palette.SlabRed[800],
    color: theme.palette.SlabGray[0],
  },
  Revision: {
    backgroundColor: theme.palette.SlabGray.Border,
    color: theme.palette.SlabGray[900],
  },
  Approved: {
    backgroundColor: theme.palette.SlabPurple[800],
    color: theme.palette.SlabGray[0],
  },
  Project: {
    backgroundColor: theme.palette.SlabGreen[800],
    color: theme.palette.SlabGray[0],
  },
  Contact: {
    backgroundColor: theme.palette.SlabPurple[600],
    color: theme.palette.SlabGray[0],
  },
});

// DOM attributes pass through so Tooltip (ref + listeners), aria-* and data-testid work; styling stays closed.
type Props = Omit<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'style' | 'color' | 'children' | 'onClick'
> & {
  label: string;
  variant: TagVariant;
  size?: TagSize;
  icon?: Icon;
  iconColor?: string;
  // Custom content around the label (a spinner, a close button); sizing it is up to the caller.
  renderLeft?: ReactNode;
  renderRight?: ReactNode;
};

export const Tag = forwardRef<HTMLDivElement, Props>(
  (
    {
      label,
      variant,
      size = 'md',
      icon: TagIcon,
      iconColor = 'currentColor',
      renderLeft,
      renderRight,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();

    const variantStyles = getVariantStyles(theme);

    const sizeStyles = {
      sm: { height: 24, borderRadius: '8px', paddingX: 1, fontSize: 14, iconSize: 16 },
      md: { height: 32, borderRadius: '8px', paddingX: 1, fontSize: 16, iconSize: 20 },
    } satisfies { [key in TagSize]: object };

    const {
      backgroundColor,
      color,
      borderColor = backgroundColor,
    }: VariantStyle = variantStyles[variant];
    const { iconSize, ...dimensions } = sizeStyles[size];

    return (
      <Chip
        {...rest}
        ref={ref}
        label={
          <>
            {renderLeft}
            <Box component='span' sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {label}
            </Box>
            {renderRight}
          </>
        }
        icon={TagIcon && <TagIcon size={iconSize} color={iconColor} />}
        sx={{
          ...dimensions,
          gap: 0.5,
          fontWeight: 400,
          backgroundColor,
          color,
          // Every variant gets a border so bordered and borderless tags share one size.
          border: `1px solid ${borderColor}`,
          '& .MuiChip-label': { padding: 0, display: 'flex', alignItems: 'center', gap: 0.5 },
          '& .MuiChip-icon': { margin: 0, color: 'inherit' },
        }}
      />
    );
  },
);
